// IMPORTANT NOTE: Please don't import other selectors from this file.
// Use secondOrder and *ThirdOrder for your selector if it has other
// dependencies.
import { createSelector } from 'reselect'

import { richContentIsNonEmpty } from '../helpers/cards'
import { nextId } from '../store/newIds'
import { createDeepEqualSelector } from './createDeepEqualSelector'
import { fullFileStateSelector, rawFileStateSelector } from './fullFileFirstOrder'

/**
 * @typedef {import('../index').PlottrState} PlottrState
 * @typedef {import('../index').Card} Card
 * @typedef {import('../index').CardMetadata} CardMetadata
 * @typedef {import('../index').Slate} Slate
 * @typedef {import('../types/system').SceneTemplate} SceneTemplate
 * @typedef {import('../types/system').SceneCustomTemplate} SceneCustomTemplate
 * @typedef {import('../types/plottr').AppliedSceneTemplate} AppliedSceneTemplate
 * @typedef {import('../types/plottr').AppliedSceneCustomTemplate} AppliedSceneCustomTemplate
 */

/**
 * @type {function(PlottrState): Array<Card>}
 */
export const allCardsSelector = createSelector(fullFileStateSelector, ({ cards }) => {
  return cards ?? []
})

/**
 * @param {PlottrState} _state
 * @param {number} id
 * @return {number}
 */
const selectId = (_state, id) => id
/**
 * @type {function(PlottrState, number): (Card | Null)}
 */
export const singleCardSelector = createSelector(
  allCardsSelector,
  selectId,
  (cards, propId) => cards.find(({ id }) => id === propId) ?? null
)

/**
 * @param {SceneTemplate | SceneCustomTemplate} template
 * @return {AppliedSceneTemplate | AppliedSceneCustomTemplate}
 */
const templateMetadata = (template) => {
  return {
    ...template,
    attributes: template.attributes.map((attribute) => {
      const { name, type, description, link } = attribute
      return { name, type, description, link, value: '' }
    }),
  }
}

/**
 * @param {Card} card
 * @return {CardMetadata}
 */
const cardMetaData = (card) => {
  const {
    id,
    beatId,
    lineId,
    tags,
    color,
    places,
    characters,
    bookId,
    title,
    templates,
    positionWithinLine,
    description,
    notes,
    audioClipId,
  } = card

  return {
    id,
    beatId,
    lineId,
    tags,
    color,
    places,
    characters,
    bookId,
    title,
    templates: templates.map(templateMetadata),
    positionWithinLine,
    hasDescription: Array.isArray(description) && richContentIsNonEmpty(description),
    notes: notes ?? [],
    audioClipId,
  }
}

/**
 * @type {function(PlottrState): Array<CardMetadata>}
 */
export const allCardMetaDataSelector = createSelector(allCardsSelector, (cards) =>
  cards.map(cardMetaData)
)

/**
 * @typedef CardIdAndKeyData
 * @property {number} id
 * @property {number} beatId
 * @property {number} lineId
 * @property {number} positionWithinLine
 */

/**
 * @param {Card} card
 * @return {CardIdAndKeyData}
 */
const cardIdAndKeyData = (card) => {
  const { id, beatId, lineId, positionWithinLine } = card

  return {
    id,
    beatId,
    lineId,
    positionWithinLine,
  }
}

/**
 * @type {function(PlottrState): Array<CardIdAndKeyData>}
 */
export const allCardIdAndKeyDataSelector = createSelector(allCardsSelector, (cards) =>
  cards.map(cardIdAndKeyData)
)

/**
 * @type {function(PlottrState): number}
 */
export const nextCardIdSelector = createSelector(allCardsSelector, (cards) => nextId(cards))

/**
 * @param {PlottrState} _state
 * @param {number} cardId
 * @return {number}
 */
const cardIdSelector = (_state, cardId) => cardId
/**
 * @type {function(PlottrState, number): (Card | Null)}
 */
export const cardByIdSelector = createSelector(
  cardIdSelector,
  allCardsSelector,
  (cardId, cards) => {
    return cards.find((card) => card.id === cardId) ?? null
  }
)

/**
 * @type {function(PlottrState, number): (Slate | String | Null)}
 */
export const cardDescriptionByIdSelector = createSelector(cardByIdSelector, (card) =>
  card ? card.description : null
)

/**
 * @type {function(PlottrState, number): CardMetadata | Null}
 */
const _cardMetaDataSelector = createSelector(cardByIdSelector, (card) => {
  if (!card) return null

  return cardMetaData(card)
})

/**
 * @type {function(PlottrState, number): CardMetadata | Null}
 */
export const cardMetaDataSelector = createDeepEqualSelector(_cardMetaDataSelector, (metadata) => {
  return metadata
})

/**
 * @type {function(number, String): function(PlottrState): (String | Slate | number)}
 */
export const attributeValueSelector = (cardId, attributeName) =>
  createSelector(
    rawFileStateSelector,
    (state) =>
      cardByIdSelector(
        state,
        // @ts-ignore
        cardId
      )?.[attributeName] ?? ''
  )

/**
 * @type {function(number, String, String): function(PlottrState): (String | Slate | number)}
 */
export const templateAttributeValueSelector = (cardId, templateId, attributeName) =>
  createSelector(rawFileStateSelector, (state) => {
    const card = cardByIdSelector(state, cardId)
    const templateOnCard = card && card.templates.find(({ id }) => id === templateId)
    const valueInAttributes =
      templateOnCard && templateOnCard.attributes.find(({ name }) => name === attributeName)?.value
    const valueOnTemplate = templateOnCard && templateOnCard[attributeName]
    return valueInAttributes || valueOnTemplate || ''
  })
